import { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText, X, Loader2, Receipt, BadgeCheck, TrendingUp, AlertTriangle,
  Paperclip, UploadCloud, CheckCheck, Zap, Trash2, Plus, FileUp, Files, Unlink,
  Eye, Download, Pencil, Lock,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatusBadge } from "./SharedComponents";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";
import { agruparPorMaterial } from "./helpers";
import {
  ocInvoices, ocCoverage, computeCoverage, ocInvoiceLabel, ocInvoiceComplete,
  COVERAGE_STYLES, COVERAGE_LABELS,
} from "../../utils/invoices";
import { BadgeCierre, VinculacionCerradaInfo, CerrarVinculacionModal, itemsConDiferencia } from "./VinculacionCerrada";   // Fix GS

const MAX_FILE_MB = 10; // BLOQUE 49 — mismo criterio que los adjuntos de presupuestos

let draftSeq = 0;
const newDraft = (extra = {}) => ({
  key: `d${++draftSeq}`,
  file: null,
  file_name: "",
  invoice_number: "",
  invoice_date: "",
  invoice_total: "",
  timbrado: "",
  tipo_documento: "Física",
  venc_timbrado: "",
  items: [],
  analyzing: false,
  analyzed: false,
  error: "",
  obs: "",
  ...extra,
});

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const draftToInvoice = (d) => ({
  invoice_number: (d.invoice_number || "").trim(),
  invoice_date: d.invoice_date || "",
  invoice_total: Number(d.invoice_total || 0),
  timbrado: d.timbrado || "",
  tipo_documento: d.tipo_documento || "Física",
  venc_timbrado: d.venc_timbrado || "",
  doc_name: d.file_name || "",
  items: (d.items || []).map(l => ({
    item_index: Number.isInteger(l.item_index) ? l.item_index : null,
    item_name: l.item_name || "",
    quantity: Number(l.quantity || 0),
    unit_price: Number(l.unit_price || 0),
    subtotal: Number(l.subtotal || 0) || Number(l.quantity || 0) * Number(l.unit_price || 0),
  })),
});

// Fix FW (2026-10-14): "En facturas también se siguen separando los ítems" (Sergio, 14/10).
// Igual que en la OC (Fix EG/FF): una fila por material con la cantidad total, aunque la OC lo
// reparta en varias líneas del presupuesto. Solo para mostrar: la factura sigue guardando la línea
// asignada y la cobertura sigue repartiéndose entre las líneas del material (Fix FE).
function coberturaPorMaterial(ocItems, coverage) {
  if (!coverage) return coverage;
  const redondear = (n) => Math.round(n * 10000) / 10000;
  const items = agruparPorMaterial(ocItems || [], true).map(g => {
    const filas = g.__idx.map(i => (coverage.items || [])[i]).filter(Boolean);
    if (!filas.length) return null;
    const ocQty = redondear(filas.reduce((s, f) => s + f.oc_quantity, 0));
    const invQty = redondear(filas.reduce((s, f) => s + f.invoiced_quantity, 0));
    let status = "falta";
    if (invQty > 0.0001) status = Math.abs(invQty - ocQty) <= 0.001 ? "ok" : (invQty > ocQty ? "exceso" : "parcial");
    return {
      ...filas[0], item_index: g.__idx[0], oc_quantity: ocQty, invoiced_quantity: invQty,
      pending_quantity: Math.max(redondear(ocQty - invQty), 0),
      oc_subtotal: filas.reduce((s, f) => s + f.oc_subtotal, 0),
      invoiced_amount: filas.reduce((s, f) => s + f.invoiced_amount, 0),
      status,
    };
  }).filter(Boolean);
  return { ...coverage, items };
}

// ── Panel de cobertura por ítem (semáforo del AP, Bloque 18) ──
function CoveragePanel({ coverage, ocTotal }) {
  const items = coverage?.items || [];
  const over = (coverage?.invoiced_total || 0) > ocTotal + 1;
  const missing = items.filter(i => i.status === "falta" || i.status === "parcial").length;
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="invoice-coverage-panel">
      <div className="bg-slate-800 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-bold uppercase tracking-wide">Cobertura de facturación</p>
        <p className="text-xs font-semibold" data-testid="coverage-header-summary">
          Facturado {formatPYG(coverage?.invoiced_total || 0)} de {formatPYG(ocTotal || 0)}
          {" · "}{coverage?.invoices_count || 0} factura{(coverage?.invoices_count || 0) === 1 ? "" : "s"}
        </p>
      </div>
      {Math.max((ocTotal || 0) - (coverage?.invoiced_total || 0), 0) > 0 && (
        <div className="px-4 py-1.5 bg-amber-50 border-b border-amber-100 text-[11px] font-bold text-amber-800" data-testid="coverage-falta-facturar">
          Falta facturar {formatPYG(Math.max((ocTotal || 0) - (coverage?.invoiced_total || 0), 0))}
        </div>
      )}
      {(over || missing > 0) && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800 flex items-start gap-2" data-testid="coverage-warning">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          <span>
            {over && <>La suma de las facturas ({formatPYG(coverage.invoiced_total)}) supera el total de la OC ({formatPYG(ocTotal)}). </>}
            {missing > 0 && <>{missing} ítem(s) sin facturar por completo. </>}
            Podés continuar igual: es solo una advertencia.
          </span>
        </div>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto max-h-56">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-gray-500">
                <th className="text-left px-3 py-2 font-semibold">Ítem de la OC</th>
                <th className="text-center px-2 py-2 font-semibold">Cant. OC</th>
                <th className="text-center px-2 py-2 font-semibold">Cant. Facturada</th>
                <th className="text-center px-2 py-2 font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(it => (
                <tr key={it.item_index} data-testid={`coverage-row-${it.item_index}`}>
                  {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                  <td className="px-3 py-1.5 text-gray-700 max-w-[280px] whitespace-normal break-words" title={it.item_name}>{it.item_name}</td>
                  <td className="px-2 py-1.5 text-center text-gray-600">{it.oc_quantity} {it.unit}</td>
                  <td className="px-2 py-1.5 text-center font-semibold text-gray-800">{it.invoiced_quantity}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${COVERAGE_STYLES[it.status]}`}
                      data-testid={`coverage-status-${it.item_index}`}>
                      {COVERAGE_LABELS[it.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(coverage?.extras || []).length > 0 && (
        <div className="px-4 py-2 bg-orange-50 border-t border-orange-200 text-[11px] text-orange-800" data-testid="coverage-extras">
          <p className="font-semibold mb-0.5">Líneas de factura sin ítem equivalente en la OC ({coverage.extras.length})</p>
          {coverage.extras.map((e, i) => (
            <p key={i}>· {e.item_name || "(sin descripción)"} — {e.quantity} un · {formatPYG(e.subtotal)} {e.invoice_number ? `(${e.invoice_number})` : ""}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Card editable de una factura a cargar ──
function DraftCard({ draft, index, ocItems, coverage, onChange, onRemove }) {
  const update = (patch) => onChange({ ...draft, ...patch });
  const grupos = agruparPorMaterial(ocItems || [], true);   // Fix FW (2026-10-14): una opción por material
  const primeraLinea = (idx) => (grupos.find(g => g.__idx.includes(idx)) || { __idx: [idx] }).__idx[0];
  const previewDraftFile = () => {
    if (!draft.file) return;
    const url = URL.createObjectURL(draft.file);
    window.open(url, "_blank");
  };
  const updateLine = (i, patch) => {
    const items = draft.items.map((l, idx) => {
      if (idx !== i) return l;
      const merged = { ...l, ...patch };
      merged.subtotal = Number(merged.quantity || 0) * Number(merged.unit_price || 0);
      return merged;
    });
    update({ items });
  };
  const addLine = () => {
    const pend = (coverage?.items || []).find(c => c.pending_quantity > 0.0001);
    const idx = pend ? pend.item_index : 0;
    const base = ocItems[idx] || {};
    update({
      items: [...draft.items, {
        item_index: ocItems.length ? idx : null,
        item_name: base.item_name || base.material_name || "",
        quantity: pend ? pend.pending_quantity : Number(base.quantity || 0),
        unit_price: Number(base.unit_price || 0),
        subtotal: (pend ? pend.pending_quantity : Number(base.quantity || 0)) * Number(base.unit_price || 0),
        confidence: 0,
      }],
    });
  };
  const removeLine = (i) => update({ items: draft.items.filter((_, idx) => idx !== i) });
  const assignedTotal = draft.items.reduce((s, l) => s + (Number(l.subtotal || 0) || Number(l.quantity || 0) * Number(l.unit_price || 0)), 0);

  return (
    <div className="rounded-xl border border-[#2E6DA4]/30 bg-white overflow-hidden" data-testid={`invoice-draft-${index}`}>
      <div className="bg-[#2E6DA4]/8 px-3 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-bold bg-[#2E6DA4] text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{index + 1}</span>
          <p className="text-xs font-semibold text-[#2E6DA4] truncate">
            {draft.invoice_number || "Factura sin número"}
            {draft.file_name ? <span className="text-gray-500 font-normal"> · {draft.file_name}</span> : null}
          </p>
          {draft.file && (
            <button onClick={previewDraftFile} title="Previsualizar el archivo antes de confirmar"
              className="text-gray-400 hover:text-[#2E6DA4] flex-shrink-0" data-testid={`preview-draft-${index}`}>
              <Eye size={13} />
            </button>
          )}
          {draft.analyzing && <Loader2 size={12} className="animate-spin text-[#2E6DA4]" />}
        </div>
        <button onClick={onRemove} className="text-gray-400 hover:text-red-500 flex-shrink-0" title="Quitar esta factura"
          data-testid={`remove-draft-${index}`}>
          <Trash2 size={13} />
        </button>
      </div>
      {draft.error && (
        <p className="px-3 py-1.5 text-[11px] text-red-700 bg-red-50 border-b border-red-100">{draft.error}</p>
      )}
      {draft.obs && (
        <p className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100">IA: {draft.obs}</p>
      )}
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">N° de Factura *</label>
            <input value={draft.invoice_number} onChange={e => update({ invoice_number: e.target.value })}
              placeholder="001-001-0001234"
              className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none font-mono"
              data-testid={index === 0 ? "inv-number-input" : `inv-number-input-${index}`} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">Fecha Factura</label>
            <input type="date" value={draft.invoice_date} onChange={e => update({ invoice_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
              data-testid={index === 0 ? "inv-date-input" : `inv-date-input-${index}`} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">Total Factura (Gs.)</label>
            <input type="number" value={draft.invoice_total} onChange={e => update({ invoice_total: e.target.value })}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
              data-testid={index === 0 ? "inv-total-input" : `inv-total-input-${index}`} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">Timbrado</label>
            <input value={draft.timbrado} onChange={e => update({ timbrado: e.target.value })} placeholder="12345678"
              className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none"
              data-testid={index === 0 ? "inv-timbrado-input" : `inv-timbrado-input-${index}`} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">Tipo de Documento</label>
            <select value={draft.tipo_documento} onChange={e => update({ tipo_documento: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:outline-none"
              data-testid={index === 0 ? "inv-tipodoc-select" : `inv-tipodoc-select-${index}`}>
              <option value="Física">Física</option>
              <option value="Electrónica">Electrónica</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1">Venc. Timbrado</label>
            <input type="date" value={draft.venc_timbrado} onChange={e => update({ venc_timbrado: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none"
              data-testid={index === 0 ? "inv-venctimbrado-input" : `inv-venctimbrado-input-${index}`} />
          </div>
        </div>

        {/* Asignación de ítems */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">
              Ítems cubiertos por esta factura ({draft.items.length})
            </p>
            <button onClick={addLine} className="text-[11px] text-[#2E6DA4] font-semibold hover:underline"
              data-testid={`add-draft-line-${index}`}>
              + Asignar ítem
            </button>
          </div>
          {draft.items.length === 0 ? (
            <p className="text-[11px] text-gray-400 italic border border-dashed border-gray-200 rounded-lg px-3 py-2">
              Sin ítems asignados — la factura se guarda igual (sin detalle por ítem).
            </p>
          ) : (
            <div className="space-y-1.5">
              {draft.items.map((l, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`draft-${index}-line-${i}`}>
                  <select value={Number.isInteger(l.item_index) ? primeraLinea(l.item_index) : ""}
                    onChange={e => {
                      const v = e.target.value;
                      const idx = v === "" ? null : Number(v);
                      const base = idx !== null ? (ocItems[idx] || {}) : {};
                      updateLine(i, {
                        item_index: idx,
                        item_name: idx !== null ? (base.item_name || base.material_name || "") : (l.item_name || ""),
                        unit_price: Number(l.unit_price || base.unit_price || 0),
                      });
                    }}
                    className={`flex-1 min-w-0 border rounded-lg px-2 py-1.5 text-[11px] bg-white ${Number.isInteger(l.item_index) ? "border-gray-300" : "border-orange-300 text-orange-700"}`}
                    data-testid={`draft-${index}-line-${i}-item`}>
                    <option value="">— Sin ítem de OC (extra: {l.item_name || "s/d"})</option>
                    {grupos.map((g, gi) => (
                      <option key={g.__idx[0]} value={g.__idx[0]}>{gi + 1}. {g.item_name || g.material_name}</option>
                    ))}
                  </select>
                  <input type="number" value={l.quantity} onChange={e => updateLine(i, { quantity: Number(e.target.value) })}
                    className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-[11px]" title="Cantidad"
                    data-testid={`draft-${index}-line-${i}-qty`} />
                  <input type="number" value={l.unit_price} onChange={e => updateLine(i, { unit_price: Number(e.target.value) })}
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-[11px]" title="Precio unitario"
                    data-testid={`draft-${index}-line-${i}-price`} />
                  <span className="w-28 text-right text-[11px] font-semibold text-gray-700">
                    {formatPYG(Number(l.subtotal || 0) || Number(l.quantity || 0) * Number(l.unit_price || 0))}
                  </span>
                  {l.confidence ? (
                    <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 py-0.5">
                      IA {Math.round(l.confidence * 100)}%
                    </span>
                  ) : null}
                  <button onClick={() => removeLine(i)} className="text-gray-300 hover:text-red-500"
                    data-testid={`draft-${index}-line-${i}-remove`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              <p className="text-[10px] text-gray-500 text-right">
                Suma de ítems asignados: <span className="font-semibold">{formatPYG(assignedTotal)}</span>
                {Number(draft.invoice_total || 0) > 0 && Math.abs(assignedTotal - Number(draft.invoice_total)) > 1 && (
                  <span className="text-amber-700"> · difiere del total de la factura ({formatPYG(Number(draft.invoice_total))})</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Fix EJ (2026-09-27): "En Facturas, habilitar edición y cierre de vinculación, para los
// casos en que el proveedor finalmente no tiene la cantidad cerrada en la OC" (CEN). Se edita
// por material, como se ve la OC; la cantidad nueva se reparte entre sus ítems del
// presupuesto en la misma proporción. Solo se puede bajar.
function EditarCantidadesOC({ oc, onClose, onSaved }) {
  const grupos = agruparPorMaterial(oc.items || [], true);
  const [cant, setCant] = useState(() => grupos.map(g => String(g.quantity)));
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const nuevas = grupos.map((g, gi) => Number(cant[gi]));
  const invalida = nuevas.some((q, gi) => !(q >= 0) || q > grupos[gi].quantity + 0.0001);
  const cambia = nuevas.some((q, gi) => Math.abs(q - grupos[gi].quantity) > 0.0001);
  const totalNuevo = grupos.reduce((s, g, gi) => s + (Number(g.unit_price) || 0) * (nuevas[gi] || 0), 0);
  const guardar = async () => {
    const items = [];
    grupos.forEach((g, gi) => {
      const nueva = nuevas[gi];
      if (Math.abs(nueva - g.quantity) <= 0.0001) return;
      const base = g.__idx.map(i => Number(oc.items[i].quantity) || 0);
      const suma = base.reduce((s, x) => s + x, 0);
      let asignado = 0;
      g.__idx.forEach((i, k) => {
        const q = k === g.__idx.length - 1 ? nueva - asignado : (suma > 0 ? (nueva * base[k]) / suma : 0);
        const r = Math.round(q * 10000) / 10000;
        asignado += r;
        items.push({ index: i, quantity: r });
      });
    });
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/oc-from-pf/${oc._id}/cantidades`, { items, motivo: motivo.trim() }, { withCredentials: true });
      toast.success(`Cantidades de ${oc.number} actualizadas`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron guardar las cantidades");
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={() => !saving && onClose()} title={`Editar cantidades · ${oc.number}`} wide>
      <div className="space-y-4" data-testid="editar-cant-oc-modal">
        <p className="text-xs text-gray-500">Para cuando el proveedor finalmente no tiene la cantidad de la OC. Solo se puede bajar; el total de la OC se recalcula.</p>
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Material</th>
                <th className="px-2 py-2">UM</th>
                <th className="px-2 py-2 text-right">Cant. OC</th>
                <th className="px-2 py-2 text-right">Cant. real</th>
                <th className="px-2 py-2 text-right">P.U.</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grupos.map((g, gi) => (
                <tr key={gi} data-testid={`editar-cant-fila-${gi}`}>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {g.item_name || g.material_name}
                    {g.__idx.length > 1 && <span className="block text-[10px] text-gray-400">{g.__idx.length} ítems del presupuesto</span>}
                  </td>
                  <td className="px-2 py-2 text-center text-gray-500">{g.unit}</td>
                  <td className="px-2 py-2 text-right">{g.quantity}</td>
                  <td className="px-2 py-2 text-right">
                    <input type="number" min="0" max={g.quantity} value={cant[gi]}
                      onChange={e => setCant(prev => prev.map((v, k) => (k === gi ? e.target.value : v)))}
                      className={`w-20 border rounded-lg px-2 py-1 text-right ${!(nuevas[gi] >= 0) || nuevas[gi] > g.quantity + 0.0001 ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                      data-testid={`editar-cant-input-${gi}`} />
                  </td>
                  <td className="px-2 py-2 text-right text-gray-600">{formatPYG(g.unit_price || 0)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatPYG((Number(g.unit_price) || 0) * (nuevas[gi] || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm font-bold text-[#2E6DA4] text-right" data-testid="editar-cant-total">
          Total OC: {formatPYG(totalNuevo)} <span className="text-xs font-normal text-gray-400">(antes {formatPYG(oc.total || 0)})</span>
        </p>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Motivo <span className="text-red-600">*</span></label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} placeholder="Ej: el proveedor entregó 15 m, no tenía más"
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="editar-cant-motivo" />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={saving} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={guardar} disabled={saving || invalida || !cambia || !motivo.trim()}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="editar-cant-guardar">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />} Guardar cantidades
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function FacturasTab({
  ocFromPF, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [matchModal, setMatchModal] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [editOC, setEditOC] = useState(null);         // Fix EJ (2026-09-27)
  const [cerrandoId, setCerrandoId] = useState(null); // Fix EJ (2026-09-27)
  const [cerrarOC, setCerrarOC] = useState(null);     // Fix GS (2026-10-17): ventana de cierre con la diferencia y el motivo
  // Cruce clásico (texto / ítems manuales) — aplica a la 1ra factura del modal
  const [showClassic, setShowClassic] = useState(false);
  const [inputMode, setInputMode] = useState("text");
  const [invoiceText, setInvoiceText] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([{ name: "", quantity: 1, unit_price: 0 }]);
  const [matchResult, setMatchResult] = useState(null);
  const [matching, setMatching] = useState(false);
  const filesRef = useRef(null);
  const attachDocRef = useRef(null);
  const [attachingInvoiceId, setAttachingInvoiceId] = useState(null);
  // C.3 — Auto-apply IA settings + countdown state
  const [aiAutoSettings, setAiAutoSettings] = useState({ enabled: true, confidence_threshold: 95, max_total_diff_pct: 5.0 });
  const [autoApplyCountdown, setAutoApplyCountdown] = useState(null);
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");
  const autoApplyTimerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/purchases/ai-auto-apply-settings`, { withCredentials: true })
      .then(r => setAiAutoSettings(r.data || {}))
      .catch(() => {});
  }, []);

  const cancelAutoApply = () => {
    if (autoApplyTimerRef.current) {
      clearInterval(autoApplyTimerRef.current);
      autoApplyTimerRef.current = null;
    }
    setAutoApplyCountdown(null);
  };

  const canMatch = roleAllowed(userRole, ["Superadministrador", "Administrador", "Compras", "Contador", "Gerente Tecnico"]);
  const canUnlink = roleAllowed(userRole, ["Superadministrador", "Administrador", "Jefe de Compras", "Compras"]);

  // BLOQUE 49 — ver/descargar/adjuntar el documento REAL de una factura ya vinculada.
  const invoiceDocUrl = (ocId, invoiceId, inline) =>
    `${API}/purchases/oc-from-pf/${ocId}/invoices/${invoiceId}/document${inline ? "?inline=1" : ""}`;
  const viewInvoiceDoc = (ocId, invoiceId) => window.open(invoiceDocUrl(ocId, invoiceId, true), "_blank");
  const downloadInvoiceDoc = (ocId, invoiceId) => window.open(invoiceDocUrl(ocId, invoiceId, false), "_blank");

  const handleAttachDoc = (invoiceId) => {
    setAttachingInvoiceId(invoiceId);
    attachDocRef.current.click();
  };

  const handleAttachDocSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !attachingInvoiceId || !matchModal) { setAttachingInvoiceId(null); return; }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`El archivo supera ${MAX_FILE_MB}MB.`);
      setAttachingInvoiceId(null);
      return;
    }
    try {
      const file_data = await readAsDataURL(file);
      await axios.put(`${API}/purchases/oc-from-pf/${matchModal._id}/invoices/${attachingInvoiceId}/document`, {
        file_data, file_name: file.name, file_type: file.type,
      }, { withCredentials: true });
      toast.success("Documento adjuntado correctamente");
      await fetchInvoices(matchModal._id);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al adjuntar el documento");
    } finally {
      setAttachingInvoiceId(null);
    }
  };

  // BLOQUE 41 — el criterio de la cola pasa a ser COBERTURA INCOMPLETA (no "sin
  // factura"): una OC con 1 de 2 facturas debe seguir acá para poder cargar la segunda.
  const matchFilter = (oc) => {
    if (!matchClienteObraPresupuesto(oc, { filterCliente, filterObra, filterPresupuesto }, { listasClientes, listasObras })) return false;
    if (filterUrgency && oc.urgency !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (oc.number || "").toLowerCase().includes(s) || (oc.project_name || "").toLowerCase().includes(s) || (oc.provider_name || "").toLowerCase().includes(s);
      if (!hit) return false;
    }
    return true;
  };
  const byDateDesc = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const pendingOCsAll = ocFromPF.filter(oc => oc.status !== "cancelada" && !ocInvoiceComplete(oc) && matchFilter(oc));
  const linkedOCsAll = ocFromPF.filter(oc => oc.status !== "cancelada" && ocInvoiceComplete(oc) && matchFilter(oc));
  // BLOQUE 50 PARTE B — pendientes de cruce arriba (ya son la 1ra sección), vinculadas
  // abajo (2da sección); dentro de cada grupo, la más nueva primero.
  const pendingOCs = (filterStatus === "vinculada" ? [] : pendingOCsAll).slice().sort(byDateDesc);
  const linkedOCs = (filterStatus === "pendiente" ? [] : linkedOCsAll).slice().sort(byDateDesc);

  const fetchInvoices = useCallback(async (ocId) => {
    try {
      const r = await axios.get(`${API}/purchases/oc-from-pf/${ocId}/invoices`, { withCredentials: true });
      setLinkedInvoices(r.data?.invoices || []);
    } catch {
      setLinkedInvoices([]);
    }
  }, []);

  const openMatch = (oc) => {
    setMatchModal(oc);
    setDrafts([]);
    setLinkedInvoices(ocInvoices(oc));
    setMatchResult(null);
    setInvoiceText("");
    setInvoiceItems([{ name: "", quantity: 1, unit_price: 0 }]);
    setInputMode("text");
    setShowClassic(false);
    cancelAutoApply();
    fetchInvoices(oc._id);
  };

  const ocItems = matchModal?.items || [];
  const ocTotal = Number(matchModal?.total || 0);
  const liveCoverage = computeCoverage(ocItems, [...linkedInvoices, ...drafts.map(draftToInvoice)]);
  const ocGrupos = agruparPorMaterial(ocItems, true);                  // Fix FW (2026-10-14): una fila por material
  const coberturaVista = coberturaPorMaterial(ocItems, liveCoverage);  // Fix FW (la IA sigue usando liveCoverage, por línea)

  // ── Carga múltiple de archivos ──
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const ok = [];
    files.forEach(f => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} supera ${MAX_FILE_MB}MB — no se agregó.`);
        return;
      }
      ok.push(newDraft({ file: f, file_name: f.name }));
    });
    if (ok.length) {
      setDrafts(p => [...p, ...ok]);
      toast.success(`${ok.length} factura(s) agregada(s). Analizá con IA o completá los datos.`);
    }
  };

  const analyzePending = async () => {
    if (!matchModal) return;
    const targets = drafts.filter(d => d.file && !d.analyzed);
    if (!targets.length) { toast.info("No hay archivos nuevos para analizar"); return; }
    setAnalyzing(true);
    cancelAutoApply();
    // Pendientes iniciales considerando lo ya vinculado + los borradores ya cargados
    let pending = liveCoverage.items.map(i => i.pending_quantity);
    for (const t of targets) {
      setDrafts(p => p.map(d => d.key === t.key ? { ...d, analyzing: true, error: "" } : d));
      try {
        const file_data = await readAsDataURL(t.file);
        const r = await axios.post(`${API}/purchases/oc-from-pf/${matchModal._id}/ai-extract-invoices`, {
          files: [{ file_data, file_name: t.file_name, file_type: t.file.type }],
          pending_override: pending,
        }, { withCredentials: true });
        const res = (r.data?.results || [])[0] || {};
        if (Array.isArray(r.data?.pending_after)) pending = r.data.pending_after;
        if (!res.ok) {
          setDrafts(p => p.map(d => d.key === t.key ? { ...d, analyzing: false, analyzed: true, error: res.error || "No se pudo analizar" } : d));
          continue;
        }
        const items = (res.asignacion || []).map(a => ({
          item_index: Number.isInteger(a.item_index) ? a.item_index : null,
          item_name: a.oc_item_name || a.item_name || "",
          quantity: Number(a.quantity || 0),
          unit_price: Number(a.unit_price || 0),
          subtotal: Number(a.subtotal || 0) || Number(a.quantity || 0) * Number(a.unit_price || 0),
          confidence: Number(a.confidence || 0),
        }));
        setDrafts(p => p.map(d => d.key === t.key ? {
          ...d,
          analyzing: false, analyzed: true, error: "",
          invoice_number: res.nro_factura || d.invoice_number,
          invoice_date: res.fecha_factura || d.invoice_date,
          invoice_total: res.total != null ? String(res.total) : d.invoice_total,
          timbrado: res.timbrado || d.timbrado,
          venc_timbrado: res.venc_timbrado || d.venc_timbrado,
          tipo_documento: res.tipo_documento === "Electrónica" ? "Electrónica" : (d.tipo_documento || "Física"),
          items,
          obs: res.obs || "",
        } : d));
      } catch (err) {
        const msg = err.response?.data?.detail || "Error al analizar la factura";
        setDrafts(p => p.map(d => d.key === t.key ? { ...d, analyzing: false, analyzed: true, error: msg } : d));
      }
    }
    setAnalyzing(false);
    toast.success("Análisis finalizado. Revisá y corregí antes de confirmar.");
  };

  // ── Cruce clásico (texto / ítems manuales) sobre la 1ra factura ──
  const addInvoiceItem = () => setInvoiceItems(p => [...p, { name: "", quantity: 1, unit_price: 0 }]);
  const removeInvoiceItem = (i) => setInvoiceItems(p => p.filter((_, idx) => idx !== i));
  const updateInvoiceItem = (i, field, value) => {
    setInvoiceItems(p => { const n = [...p]; n[i] = { ...n[i], [field]: value }; return n; });
  };

  const ensureFirstDraft = () => {
    if (drafts.length) return drafts[0];
    const d = newDraft();
    setDrafts([d]);
    return d;
  };

  const handleAIMatch = async () => {
    if (!matchModal) return;
    const first = ensureFirstDraft();
    setMatching(true);
    setMatchResult(null);
    cancelAutoApply();
    try {
      const payload = {
        invoice_number: first.invoice_number,
        invoice_date: first.invoice_date,
        invoice_total: first.invoice_total ? Number(first.invoice_total) : 0,
        invoice_text: inputMode === "text" ? invoiceText : "",
        invoice_items: inputMode === "manual" ? invoiceItems : [],
      };
      const r = await axios.post(`${API}/purchases/oc-from-pf/${matchModal._id}/ai-match`, payload, { withCredentials: true });
      setMatchResult(r.data);
      const conf = Number(r.data?.confidence_score ?? 0);
      const threshold = Number(aiAutoSettings?.confidence_threshold ?? 95);
      const maxDiffPct = Number(aiAutoSettings?.max_total_diff_pct ?? 5.0);
      const expected = Number(r.data?.total_oc ?? matchModal?.total ?? 0);
      const found = Number(r.data?.total_invoice ?? 0);
      const totalDiffPct = (expected > 0 && found > 0) ? Math.abs((found - expected) / expected) * 100 : null;
      const canAutoApply = (
        aiAutoSettings?.enabled &&
        conf >= threshold &&
        (first.invoice_number || "").trim() &&
        drafts.length <= 1 &&
        (totalDiffPct === null || totalDiffPct <= maxDiffPct)
      );
      if (canAutoApply) {
        const TOTAL_MS = 1500;
        const startedAt = Date.now();
        setAutoApplyCountdown({ remaining_ms: TOTAL_MS, total_ms: TOTAL_MS });
        autoApplyTimerRef.current = setInterval(() => {
          const remaining = TOTAL_MS - (Date.now() - startedAt);
          if (remaining <= 0) {
            cancelAutoApply();
            handleLinkInvoices();
          } else {
            setAutoApplyCountdown({ remaining_ms: remaining, total_ms: TOTAL_MS });
          }
        }, 100);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en el análisis IA");
    } finally { setMatching(false); }
  };

  const handleLinkInvoices = async () => {
    if (!matchModal) return;
    const valid = drafts.filter(d => (d.invoice_number || "").trim());
    if (!valid.length) { toast.error("Ingrese el número de al menos una factura"); return; }
    if (valid.length !== drafts.length) { toast.error("Hay facturas sin número: completalas o quitalas"); return; }
    setLinking(true);
    try {
      const r = await axios.post(`${API}/purchases/oc-from-pf/${matchModal._id}/link-invoices`, {
        invoices: valid.map(draftToInvoice),
      }, { withCredentials: true });
      // BLOQUE 49 — subir el documento (PDF/foto) de cada factura recién vinculada.
      // `invoices` en la OC actualizada queda [existentes..., nuevas...] en el mismo
      // orden que se enviaron: las últimas `valid.length` son las que acabamos de crear.
      const allInvoices = r.data?.invoices || [];
      const createdInvoices = allInvoices.slice(-valid.length);
      const withFiles = valid.map((d, i) => ({ draft: d, invoice: createdInvoices[i] })).filter(x => x.draft.file && x.invoice);
      if (withFiles.length) {
        const results = await Promise.allSettled(withFiles.map(async ({ draft, invoice }) => {
          const file_data = await readAsDataURL(draft.file);
          await axios.put(`${API}/purchases/oc-from-pf/${matchModal._id}/invoices/${invoice.invoice_id}/document`, {
            file_data, file_name: draft.file_name, file_type: draft.file.type,
          }, { withCredentials: true });
        }));
        const failed = results.filter(x => x.status === "rejected").length;
        if (failed) toast.error(`${failed} documento(s) no se pudieron adjuntar. Podés reintentar desde "Agregar / Ver facturas".`);
      }
      toast.success(valid.length === 1 ? "Factura vinculada correctamente a la OC" : `${valid.length} facturas vinculadas a la OC`);
      setMatchModal(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vincular");
    } finally { setLinking(false); }
  };

  const handleUnlink = async (inv) => {
    if (!matchModal) return;
    if (!window.confirm(`¿Desvincular la factura ${inv.invoice_number} de la OC ${matchModal.number}? Queda registrado en el historial.`)) return;
    setUnlinkingId(inv.invoice_id || inv.invoice_number);
    try {
      await axios.delete(`${API}/purchases/oc-from-pf/${matchModal._id}/invoices/${inv.invoice_id || inv.invoice_number}`, { withCredentials: true });
      toast.success("Factura desvinculada");
      await fetchInvoices(matchModal._id);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al desvincular");
    } finally { setUnlinkingId(null); }
  };

  // Fix EJ (2026-09-27): cierra la vinculación con lo facturado hasta ahora (la OC queda
  // facturada aunque haya diferencias con las facturas); se puede reabrir.
  const handleVinculacion = async (oc, cerrar, motivo = "") => {
    // Fix GS (2026-10-17): el cierre se pide en su ventana (CerrarVinculacionModal), con la diferencia a la vista.
    if (cerrar && !motivo) { setCerrarOC(oc); return; }
    if (!cerrar && !window.confirm(`¿Reabrir la vinculación de facturas de la OC ${oc.number}?`)) return;
    setCerrandoId(oc._id);
    try {
      await axios.put(`${API}/purchases/oc-from-pf/${oc._id}/vinculacion-facturas`, { cerrar, motivo }, { withCredentials: true });
      toast.success(cerrar ? `Vinculación cerrada: ${oc.number} queda facturada` : `Vinculación reabierta: ${oc.number}`);
      setCerrarOC(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar la vinculación");
    } finally { setCerrandoId(null); }
  };

  const renderOCCard = (oc, pendingSection) => {
    const cov = ocCoverage(oc);
    const invs = ocInvoices(oc);
    return (
      <div key={oc._id} className={`bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-all ${pendingSection ? "border-gray-200/60" : "border-emerald-200/60"}`}
        data-testid={pendingSection ? "factura-oc-card" : "linked-oc-card"}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-gray-900 text-sm font-mono">{oc.number}</p>
              <StatusBadge status={oc.status} />
              {invs.length > 1 && (
                <span className="text-[10px] bg-[#2E6DA4]/10 text-[#2E6DA4] border border-[#2E6DA4]/20 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                  data-testid={`oc-invoices-count-${oc._id}`}>
                  <Files size={10} /> {invs.length} facturas
                </span>
              )}
              {!pendingSection && !oc.vinculacion_facturas_cerrada && (
                <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                  <BadgeCheck size={10} /> IA Verificada
                </span>
              )}
              {oc.vinculacion_facturas_cerrada && <BadgeCierre doc={oc} cov={cov} testId={`oc-vinc-cerrada-${oc._id}`} />}{/* Fix GS */}
              {pendingSection && invs.length > 0 && (
                <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold"
                  data-testid={`oc-partial-badge-${oc._id}`}>
                  Facturada parcialmente
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-0.5">{oc.project_name} · <span className="font-medium">{oc.provider_name}</span></p>
            <p className="text-sm font-bold text-[#2E6DA4] mt-0.5">{formatPYG(oc.total || 0)}</p>
            {invs.length > 0 && (
              <p className="text-xs text-emerald-600 font-medium mt-0.5" data-testid={`oc-invoice-line-${oc._id}`}>
                Factura{invs.length > 1 ? "s" : ""}: {ocInvoiceLabel(oc)}
                {" · "}{formatPYG(cov.invoiced_total || 0)} de {formatPYG(oc.total || 0)}
                {oc.invoice_linked_at ? ` · ${formatDate(oc.invoice_linked_at)}` : ""}
              </p>
            )}
            {cov.pending_total > 0 && !oc.vinculacion_facturas_cerrada && (
              <p className="text-xs text-amber-700 font-bold mt-0.5" data-testid={`oc-falta-facturar-${oc._id}`}>
                Facturado {formatPYG(cov.invoiced_total || 0)} · Falta facturar {formatPYG(cov.pending_total)}
              </p>
            )}
            <VinculacionCerradaInfo doc={oc} cov={cov} testId={`oc-vinc-motivo-${oc._id}`} />{/* Fix GS */}
            {oc.pf_numbers?.length > 0 && pendingSection && (
              <div className="flex gap-1 mt-1 flex-wrap">
                {oc.pf_numbers.map((pn, i) => (
                  <span key={i} className="text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full font-mono">{pn}</span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {/* Fix EJ (2026-09-27): editar cantidades y cerrar la vinculación con diferencias */}
            {pendingSection && canUnlink && !oc.payment_order_id && (
              <button onClick={() => setEditOC(oc)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-amber-300 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                data-testid={`editar-cant-oc-btn-${oc._id}`}>
                <Pencil size={13} /> Editar cantidades
              </button>
            )}
            {pendingSection && canUnlink && invs.length > 0 && (
              <button onClick={() => handleVinculacion(oc, true)} disabled={cerrandoId === oc._id}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50"
                data-testid={`cerrar-vinc-btn-${oc._id}`}>
                <Lock size={13} /> Cerrar vinculación
              </button>
            )}
            {!pendingSection && canUnlink && oc.vinculacion_facturas_cerrada && (
              <button onClick={() => handleVinculacion(oc, false)} disabled={cerrandoId === oc._id}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50"
                data-testid={`reabrir-vinc-btn-${oc._id}`}>
                Reabrir vinculación
              </button>
            )}
            {canMatch && (
              <button onClick={() => openMatch(oc)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${pendingSection ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                data-testid={pendingSection ? `cruzar-factura-btn-${oc._id}` : `manage-invoices-btn-${oc._id}`}>
                <TrendingUp size={13} /> {invs.length > 0 ? "Agregar / Ver facturas" : "Cruzar con Factura"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-emerald-50/70 rounded-xl border border-emerald-100 p-4 text-xs text-emerald-800">
        <p className="font-semibold mb-1">Cruce Automático de Facturas con la OC</p>
        <p>Una misma OC puede tener <span className="font-semibold">varias facturas</span> (retiros en distintas sucursales). Subí todas juntas: la IA extrae los datos y propone a qué ítem de la OC corresponde cada línea.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <FiltroClienteObraPresupuesto
          listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
          filterCliente={filterCliente} setFilterCliente={setFilterCliente}
          filterObra={filterObra} setFilterObra={setFilterObra}
          filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
          testIdPrefix="facturas"
        />
        <BusquedaEstadoUrgenciaFilters
          search={search} setSearch={setSearch}
          searchPlaceholder="Buscar por N° OC, obra o proveedor..."
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          statusOptions={[
            { value: "todos", label: `Todos (${pendingOCsAll.length + linkedOCsAll.length})` },
            { value: "pendiente", label: `Pendientes de cruce (${pendingOCsAll.length})` },
            { value: "vinculada", label: `Vinculadas (${linkedOCsAll.length})` },
          ]}
          filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
          testIdPrefix="facturas"
        />
        {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
          <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
            data-testid="facturas-clear-filter">
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : pendingOCs.length === 0 && linkedOCs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <Receipt size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay OCs disponibles para facturar</p>
          <p className="text-xs text-gray-400 mt-1">Primero genere y apruebe Órdenes de Compra desde la pestaña anterior</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingOCs.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                Pendientes / Parcialmente facturadas ({pendingOCs.length})
              </h3>
              <div className="space-y-3">{pendingOCs.map(oc => renderOCCard(oc, true))}</div>
            </div>
          )}
          {linkedOCs.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Facturadas ({linkedOCs.length})</h3>
              <div className="space-y-3">{linkedOCs.map(oc => renderOCCard(oc, false))}</div>
            </div>
          )}
        </div>
      )}

      {/* Modal: carga y cruce de facturas */}
      {matchModal && (
        <Modal open={!!matchModal} onClose={() => { setMatchModal(null); cancelAutoApply(); }}
          title={`Facturas de la OC: ${matchModal.number}`} extraWide>
          <div className="space-y-5">
            <input ref={filesRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleFilesSelected} />
            <input ref={attachDocRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAttachDocSelected} />

            {/* Resumen OC */}
            <div className="bg-[#2E6DA4]/8 rounded-xl border border-[#2E6DA4]/20 p-4">
              <p className="text-xs font-bold text-[#2E6DA4] mb-2 uppercase tracking-wide">Orden de Compra</p>
              <div className="flex flex-wrap gap-4 text-xs text-gray-700">
                <span><span className="font-semibold">Proveedor:</span> {matchModal.provider_name}</span>
                <span><span className="font-semibold">Total OC:</span> {formatPYG(ocTotal)}</span>
                <span><span className="font-semibold">Proyecto:</span> {matchModal.project_name}</span>
              </div>
              <div className="mt-2">
                <p className="text-[10px] text-gray-500 mb-1 font-semibold uppercase">Ítems OC ({ocGrupos.length})</p>
                <div className="flex flex-wrap gap-1">
                  {ocGrupos.map((item, i) => (
                    <span key={i} className="text-[10px] bg-[#2E6DA4]/10 text-[#2E6DA4] border border-[#2E6DA4]/20 px-2 py-0.5 rounded-lg">
                      {i + 1}. {item.item_name || item.material_name} ×{item.quantity}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Cobertura */}
            <CoveragePanel coverage={coberturaVista} ocTotal={ocTotal} />
            <VinculacionCerradaInfo doc={matchModal} cov={ocCoverage(matchModal)} testId="oc-modal-vinc-cerrada" />{/* Fix GS */}

            {/* Facturas ya vinculadas */}
            {linkedInvoices.length > 0 && (
              <div className="rounded-xl border border-emerald-200 overflow-hidden" data-testid="linked-invoices-section">
                <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200">
                  <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Facturas ya vinculadas ({linkedInvoices.length})</p>
                </div>
                <div className="divide-y divide-emerald-100">
                  {linkedInvoices.map((inv, i) => (
                    <div key={inv.invoice_id || i} className="px-4 py-2 flex items-center justify-between gap-3 text-xs"
                      data-testid={`linked-invoice-${i}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 font-mono">
                          {inv.invoice_number}
                          {inv.fac_number && <span className="ml-2 text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded font-mono">{inv.fac_number}</span>}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {inv.invoice_date ? formatDate(inv.invoice_date) : "sin fecha"}
                          {inv.timbrado ? ` · Timbrado ${inv.timbrado}` : ""}
                          {(inv.items || []).length ? ` · ${inv.items.length} ítem(s)` : " · sin detalle de ítems"}
                          {inv.linked_by_name ? ` · ${inv.linked_by_name}` : ""}
                        </p>
                        {/* BLOQUE 49 — estado del documento real de la factura */}
                        {inv.doc_available ? (
                          <p className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5" data-testid={`invoice-doc-status-${i}`}>
                            <Paperclip size={10} /> {inv.doc_name || "Documento"}
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-400 italic flex items-center gap-1 mt-0.5" data-testid={`invoice-doc-status-${i}`}>
                            <AlertTriangle size={10} /> Documento no disponible{inv.doc_name ? ` (${inv.doc_name})` : ""}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="font-bold text-gray-800">{formatPYG(inv.invoice_total || 0)}</span>
                        {inv.doc_available ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => viewInvoiceDoc(matchModal._id, inv.invoice_id)}
                              className="flex items-center gap-1 text-[11px] text-[#2E6DA4] border border-[#2E6DA4]/30 hover:bg-[#2E6DA4]/10 rounded-lg px-2 py-1"
                              title="Ver documento" data-testid={`view-invoice-doc-${i}`}>
                              <Eye size={11} /> Ver
                            </button>
                            <button onClick={() => downloadInvoiceDoc(matchModal._id, inv.invoice_id)}
                              className="flex items-center gap-1 text-[11px] text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg px-2 py-1"
                              title="Descargar documento" data-testid={`download-invoice-doc-${i}`}>
                              <Download size={11} /> Descargar
                            </button>
                          </div>
                        ) : canMatch && (
                          <button onClick={() => handleAttachDoc(inv.invoice_id)} disabled={attachingInvoiceId === inv.invoice_id}
                            className="flex items-center gap-1 text-[11px] text-gray-500 border border-dashed border-gray-300 hover:bg-gray-50 rounded-lg px-2 py-1 disabled:opacity-50"
                            title="Adjuntar documento de esta factura" data-testid={`attach-invoice-doc-${i}`}>
                            {attachingInvoiceId === inv.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />} Adjuntar
                          </button>
                        )}
                        {canUnlink && (
                          <button onClick={() => handleUnlink(inv)} disabled={unlinkingId === (inv.invoice_id || inv.invoice_number)}
                            className="flex items-center gap-1 text-[11px] text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-2 py-1 disabled:opacity-50"
                            data-testid={`unlink-invoice-${i}`}>
                            {unlinkingId === (inv.invoice_id || inv.invoice_number)
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Unlink size={11} />} Desvincular
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Carga múltiple */}
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-3">
              <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                <Zap size={12} className="text-emerald-600" /> Cargar y analizar facturas con IA
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => filesRef.current?.click()}
                  className="flex items-center gap-1.5 text-xs bg-white border border-emerald-300 text-emerald-700 px-3 py-2 rounded-xl font-semibold hover:bg-emerald-50 transition-all"
                  data-testid="select-invoice-files-btn">
                  <FileUp size={13} /> Seleccionar facturas (varias)
                </button>
                <button onClick={() => setDrafts(p => [...p, newDraft()])}
                  className="flex items-center gap-1.5 text-xs bg-white border border-gray-300 text-gray-600 px-3 py-2 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                  data-testid="add-manual-invoice-btn">
                  <Plus size={13} /> Factura manual
                </button>
                <button onClick={analyzePending} disabled={analyzing || !drafts.some(d => d.file && !d.analyzed)}
                  className="flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-2 rounded-xl font-semibold transition-all"
                  data-testid="ai-extract-factura-btn">
                  {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                  {analyzing ? "Analizando..." : `Analizar con IA (${drafts.filter(d => d.file && !d.analyzed).length})`}
                </button>
              </div>
              <p className="text-[10px] text-emerald-700 mt-1.5">
                Máx. {MAX_FILE_MB}MB por archivo. La IA extrae N° de factura, timbrado, fecha, total y propone la asignación de ítems contra lo que falta facturar.
              </p>
            </div>

            {/* Borradores */}
            {drafts.length > 0 && (
              <div className="space-y-3" data-testid="invoice-drafts">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Facturas a vincular ({drafts.length})</p>
                {drafts.map((d, i) => (
                  <DraftCard key={d.key} draft={d} index={i} ocItems={ocItems} coverage={coberturaVista}
                    onChange={(nd) => setDrafts(p => p.map(x => x.key === d.key ? nd : x))}
                    onRemove={() => setDrafts(p => p.filter(x => x.key !== d.key))} />
                ))}
              </div>
            )}

            {/* Cruce clásico (opcional) */}
            <div className="rounded-xl border border-gray-200">
              <button onClick={() => setShowClassic(s => !s)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                data-testid="toggle-classic-match">
                <span className="flex items-center gap-2"><FileText size={13} /> Cruce IA por texto / ítems manuales (clásico)</span>
                <span className="text-gray-400">{showClassic ? "Ocultar" : "Mostrar"}</span>
              </button>
              {showClassic && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                  <div className="flex gap-2">
                    {[{ id: "text", label: "Pegar texto" }, { id: "manual", label: "Ingresar ítems" }].map(m => (
                      <button key={m.id} onClick={() => setInputMode(m.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${inputMode === m.id ? "bg-[#2E6DA4] text-white" : "border border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                        data-testid={`mode-${m.id}`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  {inputMode === "text" ? (
                    <textarea value={invoiceText} onChange={e => setInvoiceText(e.target.value)}
                      rows={4} placeholder="Ej:&#10;Cable 6mm²: 100m @ 15.000 Gs&#10;Disyuntor 25A: 10 un @ 85.000 Gs"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none font-mono"
                      data-testid="invoice-text-input" />
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-gray-500">Ítems de la factura</label>
                        <button onClick={addInvoiceItem} className="text-xs text-[#2E6DA4] font-medium hover:underline">+ Agregar ítem</button>
                      </div>
                      {invoiceItems.map((item, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input placeholder="Nombre del ítem" value={item.name} onChange={e => updateInvoiceItem(i, "name", e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid={`inv-item-name-${i}`} />
                          <input type="number" placeholder="Cant." value={item.quantity} onChange={e => updateInvoiceItem(i, "quantity", Number(e.target.value))}
                            className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          <input type="number" placeholder="P.U." value={item.unit_price} onChange={e => updateInvoiceItem(i, "unit_price", Number(e.target.value))}
                            className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          {invoiceItems.length > 1 && (
                            <button onClick={() => removeInvoiceItem(i)} className="p-1.5 text-red-400 hover:text-red-600"><X size={14} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={handleAIMatch}
                    disabled={matching || (!invoiceText.trim() && !invoiceItems.some(i => i.name))}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 shadow-sm"
                    data-testid="ai-match-btn">
                    {matching ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />}
                    {matching ? "Analizando con IA..." : "Analizar con Inteligencia Artificial"}
                  </button>

                  {matchResult && (
                    <div className="space-y-3">
                      {autoApplyCountdown && (
                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-xl p-3 flex items-center gap-3 shadow-sm" data-testid="ai-autoapply-banner">
                          <CheckCheck size={20} className="text-emerald-600 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-bold text-emerald-800">Coincidencia automática detectada</p>
                            <p className="text-[11px] text-emerald-700">Aplicando en {Math.ceil(autoApplyCountdown.remaining_ms / 100) / 10}s · confianza {matchResult.confidence_score ?? "—"}%</p>
                            <div className="mt-1 h-1 bg-emerald-200/50 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 transition-all duration-100" style={{ width: `${100 - (autoApplyCountdown.remaining_ms / autoApplyCountdown.total_ms) * 100}%` }} />
                            </div>
                          </div>
                          <button onClick={cancelAutoApply}
                            className="text-xs bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-lg font-semibold"
                            data-testid="ai-autoapply-cancel-btn">
                            Cancelar
                          </button>
                        </div>
                      )}
                      <div className={`rounded-xl p-4 border ${matchResult.overall_match ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-sm font-bold ${matchResult.overall_match ? "text-emerald-700" : "text-amber-700"}`}>
                            {matchResult.overall_match ? "Cruce exitoso" : "Cruce con observaciones"}
                          </p>
                          <div className="flex items-center gap-2">
                            {matchResult.confidence_score != null && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200" data-testid="ai-confidence-badge">
                                IA {matchResult.confidence_score}% conf.
                              </span>
                            )}
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${matchResult.overall_match ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"}`}>
                              {matchResult.match_percentage || 0}% coincidencia
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600">{matchResult.summary}</p>
                        {matchResult.total_invoice != null && (
                          <div className="flex gap-4 mt-2 text-xs">
                            <span className="text-gray-500">Total OC: <span className="font-semibold text-gray-800">{formatPYG(matchResult.total_oc || 0)}</span></span>
                            <span className="text-gray-500">Total Factura: <span className="font-semibold text-gray-800">{formatPYG(matchResult.total_invoice || 0)}</span></span>
                          </div>
                        )}
                      </div>
                      {matchResult.match_items?.length > 0 && (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-800 text-white">
                                {["Ítem OC", "Ítem Factura", "Cant. OC", "Cant. Fact.", "Precio OC", "Precio Fact.", "Match"].map(h => (
                                  <th key={h} className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {matchResult.match_items.map((item, i) => (
                                <tr key={i} className={item.matched ? "bg-emerald-50/40" : "bg-red-50/30"}>
                                  {/* Fix AI (2026-09-04): descripciones completas para poder cotejar. */}
                                  <td className="px-3 py-2.5 font-medium text-gray-800 max-w-[160px] whitespace-normal break-words" title={item.oc_item}>{item.oc_item}</td>
                                  <td className="px-3 py-2.5 text-gray-600 max-w-[160px] whitespace-normal break-words" title={item.invoice_item || ""}>{item.invoice_item || <span className="text-gray-300 italic">No encontrado</span>}</td>
                                  <td className="px-3 py-2.5 text-center text-gray-700">{item.oc_quantity ?? "—"}</td>
                                  <td className="px-3 py-2.5 text-center text-gray-700">{item.invoice_quantity ?? "—"}</td>
                                  <td className="px-3 py-2.5 text-right text-gray-700">{item.oc_price ? formatPYG(item.oc_price) : "—"}</td>
                                  <td className="px-3 py-2.5 text-right text-gray-700">{item.invoice_price ? formatPYG(item.invoice_price) : "—"}</td>
                                  <td className="px-3 py-2.5 text-center">
                                    {item.matched
                                      ? <BadgeCheck size={15} className="text-emerald-500 mx-auto" />
                                      : <AlertTriangle size={15} className="text-amber-500 mx-auto" />}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => { setMatchModal(null); cancelAutoApply(); }}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
                Cerrar
              </button>
              <button onClick={handleLinkInvoices} disabled={linking || !drafts.some(d => (d.invoice_number || "").trim())}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                data-testid="confirm-link-btn">
                {linking ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />}
                {linking ? "Vinculando..." : drafts.length > 1 ? `Confirmar y Vincular ${drafts.length} Facturas` : "Confirmar y Vincular Factura"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Fix EJ (2026-09-27) */}
      {editOC && <EditarCantidadesOC oc={editOC} onClose={() => setEditOC(null)} onSaved={() => { setEditOC(null); onRefresh(); }} />}
      {cerrarOC && (   /* Fix GS */
        <CerrarVinculacionModal doc={cerrarOC} etiqueta={`La OC ${cerrarOC.number}`}
          total={ocCoverage(cerrarOC).oc_total ?? cerrarOC.total} facturado={ocCoverage(cerrarOC).invoiced_total}
          items={itemsConDiferencia({}, ocCoverage(cerrarOC))}
          onConfirm={(motivo) => handleVinculacion(cerrarOC, true, motivo)} onClose={() => setCerrarOC(null)} />
      )}
    </div>
  );
}
